<template>
	<LazyLoadWrapper>
		<div class="newsletter pt-10 w-auto bg-gray-100">
			<div
				class="main-wrapper flex md:flex-row flex-col pt-5 pb-10 gap-8 border-t border-gray-300"
			>
				<div
					class="flex w-full md:w-7/12 flex-col gap-4 md:text-left text-center"
				>
					<h2 class="text-black text-[15px] font-bold">{{ blok.heading }}</h2>
					<StoryblokComponent
						v-for="contact_us in blok.contact_us"
						:key="contact_us._uid"
						:blok="contact_us"
						class="text-sm"
					/>
				</div>
				<div class="flex w-full md:w-5/12 flex-col gap-4">
					<StoryblokComponent
						v-for="privacy_policy in blok.privacy_policy"
						:key="privacy_policy._uid"
						:blok="privacy_policy"
						class="md:text-right text-center text-black text-sm"
					/>
					<div class="flex md:justify-end justify-center items-center">
						<form
							class="flex md:justify-end justify-center items-center w-full"
							@submit.prevent="submit"
						>
							<input
								v-model="email"
								type="email"
								class="rounded-l-4xl pl-4 bg-gray-100 focus:bg-gray-200 w-full h-10 text-gray-800 placeholder:text-gray-800 placeholder:text-xs text-xs border md:max-w-[200px] max-w-full"
								:placeholder="blok.placeholder || $t('Email address')"
								name="email"
							/>
							<button
								class="button-blue-darker h-10 rounded-r-4xl button cursor-pointer inline-flex items-center justify-center px-5"
								type="submit"
							>
								{{ blok.button_text || $t("Subscribe") }}
							</button>
						</form>
					</div>
				</div>
			</div>
		</div>
	</LazyLoadWrapper>
</template>

<script setup lang="ts">
import { useToast } from "vue-toast-notification";
import type { BlokNewsletter } from "~/types/storyblok/storyblok";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";

interface Props {
	blok: BlokNewsletter;
}
const { blok } = defineProps<Props>();
const { t } = useI18n();
const toast = useToast();
const { executeRecaptcha } = useRecaptcha();

const { identify } = useDotdigital();
const email = ref("");

const submit = async () => {
	if (!email.value) {
		toast.error(t("E-mail is required."));
		return;
	}
	// Dotdigital: capture the entered email (no-op unless enabled + consent).
	identify({ email: email.value });
	try {
		const recaptchaToken = await executeRecaptcha("newsletter_subscribe");
		const response = await $fetch("/api/newsletter/subscribe", {
			method: "POST",
			body: { email: email.value, recaptchaToken }
		});
		if (response.error) {
			console.error("Error from server:", response.error);
			if (response.error.includes("409 Conflict")) {
				toast.info(t("This email is already subscribed."));
			} else {
				// The server error carries internals ("Klaviyo API key is not
				// set") — logged above, never shown to the shopper.
				toast.error(t("newsletter_subscribe_failed"));
			}
			return;
		}
		if (response.info) {
			toast.warning(t(response.info));
		}
		if (response.success) {
			toast.success(t("You have subscribed."));
		}
	} catch (error) {
		console.error("Subscription failed:", error);
		// The endpoint throws for the two cases the shopper can act on: a
		// reCAPTCHA block and the per-IP rate limit. Telling either of them to
		// "try again later" is the wrong advice, so they keep their own message
		// (AGENTS.md > reCAPTCHA, same shape as BackInStockForm).
		const status = (error as { statusCode?: number })?.statusCode;
		if (status === 403) {
			toast.error(t("recaptcha_failed"));
		} else if (status === 429) {
			toast.warning(t("back_in_stock.rate_limited"));
		} else {
			toast.error(t("newsletter_subscribe_failed"));
		}
	}
};
</script>
